import { useState, useEffect } from 'react';
import DataTable from '../DataTable';
import { useDispatch, useSelector } from 'react-redux';
import {
    convertFromStartOfDayUTC,
    convertToEndOfDayUTC,
    getBalanceSign,
    getCryptoStatus,
    getCurrencySymbol,
    getStatus,
    toFixedTrunc,
    useJunoCommonFunctions,
} from '../../../client/Juno/helpers';
import { apiService } from '../../../client/common/apiCallService';
import * as actions from '../../../client/Redux/actions';
import { format } from 'date-fns';
import Icon from '../../../client/Juno/client/icons/Icon';
import {
    cryptoCurrencyList,
    ErrorTransactions,
    fiatCurrencyList,
    statusStyle,
} from '../../helpers/constant';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import TransactionMobileComponent from './TransactionMobileComponent';
import Pagination from '../Pagination';
import TransactionExpanded from './TransactionExpanded';
import JunoButton from '../common/JunoButton';
import FilterWrapper from './FilterWrapper';
import { constant } from '../../../client/Juno/client/constant';
import EmptyStateScreen from '../common/EmptyStateScreen';

const TransactionsHistoryTable = ({ isDashboard, currentFilters, windowWidth }) => {
    const dispatch = useDispatch();
    const { getCryptoIcons, getFiatIcons } = useJunoCommonFunctions();

    const activeCurrencyPage = useSelector(state => state.juno.activeCurrencyPage);
    const clientData = useSelector(state => state.juno.clientData);
    const API_URL = useSelector(state => state.config.api_url);
    const data = useSelector(state => state.juno.allTransactions);
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [totalTransactions, setTotalTransactions] = useState(0);
    const [limit, setLimit] = useState(null);
    const image_base_url = useSelector(state => state.config.image_base_url);

    function handleSeeAll() {
        navigate('/client/transactions');
    }

    const refetchData = () => {
        if (!limit) return;
        const currencyType = activeCurrencyPage === 'crypto' ? 'crypto' : 'all';
        const from = currentFilters?.from ? convertFromStartOfDayUTC(currentFilters.from) : '';
        const to = currentFilters?.to ? convertToEndOfDayUTC(currentFilters.to) : '';

        const currency = currentFilters?.currency?.length > 0 
            ? currentFilters?.currency.map(c => c.currencyShortName) 
            : '';

        const status = currentFilters?.status?.length > 0 ? currentFilters.status : '';
        
        let type = '';
        if (currentFilters?.type?.length) {

            // Send all selected transaction types as an array
            const selectedType = currentFilters.type;
            type = selectedType.find(x => x === 'Transfer')
                ? [...selectedType.filter(type => type != 'Buy' || type != 'Sell'), 'Buy', 'Sell']
                : selectedType.length > 1
                  ? selectedType
                  : selectedType[0];
        } else if (activeCurrencyPage === 'fiat') {
            type = [...Object.values(constant.TRANSACTION_TYPE_CONSTANT.FIAT), , 'Buy', 'Sell'];
        }

        if (clientData) {
            apiService(
                (window.location.hostname === 'localhost' ? API_URL : '') +
                    '/restapi/get-client-transactions',
                {
                    currencyType,
                    from,
                    to,
                    currency,
                    status,
                    type,
                    page: currentPage,
                    pageSize: limit,
                },
                async data => {
                    if (data) {
                        dispatch(actions.setTransactionsData(data.clientsTransactionData));
                        setTotalTransactions(data?.totalCount ?? totalTransactions);
                    }
                },
                err => {
                    console.log(err);
                },
            );
        }
    };
    // Updated useEffect to watch currentFilters instead of filter
    useEffect(() => {
        refetchData();
    }, [currentFilters, clientData, activeCurrencyPage, currentPage, limit]);

    useEffect(() => {
        windowWidth > 1100 ? setLimit(10) : setLimit(8);
    }, [windowWidth]);

    const transformedStatementsData = (isDashboard ? data?.slice(0, 5) : data)?.map(item => {
        let transform = item;
        transform.remitterName = transform.transactionDetails[0]?.remitterName || 'N/A';
        transform.beneficiaryName = transform.transactionDetails[0]?.beneficiaryName || 'N/A';
        return transform;
    });

    const overviewTableConfig = {
        columns: [
            { key: 'type', label: 'Transaction Type', iconKey: 'typeIcon', noSort: true },
            { key: 'createdDate', label: 'Date', noSort: false },
            { key: 'transactionId', label: 'Transaction Id', noSort: true },
            ...(activeCurrencyPage === 'fiat'
                ? [
                      { key: 'remitterName', label: 'Remitter Name', noSort: true },
                      { key: 'beneficiaryName', label: 'Beneficiary Name', noSort: true },
                  ]
                : []),
            { key: 'currency', label: 'Currency', noSort: true },
            { key: 'amount', label: 'Amount', noSort: false },
            { key: 'status', label: 'Status', noSort: true },
        ],

        customRenderCell: (col, row) => {            
            if (col.key === 'status') {
                const statusText =
                    row?.currencyType === 'fiat'
                        ? getStatus(row.status?.code, row?.type)
                        : getCryptoStatus(row?.status?.code, row.status?.message);

                const statusKey = statusText.toUpperCase().replace('/', '');

                return (
                    <span
                        className={`px-2 py-1 text-xs font-semibold rounded-sm ${statusStyle[statusKey]}`}>
                        {statusText}
                    </span>
                );
            }

            if (col.key === 'type') {
                return (
                    <div className="flex items-center gap-[10px]" data-e2e-element={row.transactionId} data-e2e={activeCurrencyPage === 'fiat' && row.type === 'Buy'
                        ? 'Transfer (Out)'
                        : activeCurrencyPage === 'fiat' && row.type === 'Sell'
                          ? 'Transfer (In)'
                          : row.type + (row?.transferInOut ? ` (${row.transferInOut})` : '')}>
                        {row.currencyType === 'fiat'
                            ? getFiatIcons(row.type)
                            : getCryptoIcons(row.type)}
                        {activeCurrencyPage === 'fiat' && row.type === 'Buy'
                            ? 'Transfer (Out)'
                            : activeCurrencyPage === 'fiat' && row.type === 'Sell'
                              ? 'Transfer (In)'
                              : row.type + (row?.transferInOut ? ` (${row.transferInOut})` : '')}
                        {/* {row.type + (row?.transferInOut ? ` (${row.transferInOut})` : '')} */}
                    </div>
                );
            }

            if (col.key === 'createdDate') {
                return (
                    <span>
                        {row?.createdDate ? (
                            <>
                                <span>{format(new Date(row.createdDate), 'dd.MM.y')}</span>
                                <span className="ml-2">
                                    {format(new Date(row.createdDate), 'HH:mm')}
                                </span>
                            </>
                        ) : (
                            'N/A'
                        )}
                    </span>
                );
            }

            if (col.key === 'currency') {
                const isFX = row.type === 'FX';
                const isExchange = row.type === 'Exchange';

                const fromSymbol =
                    row.transactionDetails?.[0]?.fromCurrency ||
                    row.transactionDetails?.[0]?.fromCryptoId;
                const toSymbol =
                    row.transactionDetails?.[0]?.toCurrency ||
                    row.transactionDetails?.[0]?.toCryptoId;

                let matchedCurrency1 = null;
                let matchedCurrency2 = null;

                if (isFX) {
                    matchedCurrency1 = fiatCurrencyList?.find(
                        x => x.currencyShortName === fromSymbol,
                    );
                    matchedCurrency2 = fiatCurrencyList?.find(
                        x => x.currencyShortName === toSymbol,
                    );
                } else if (isExchange) {
                    matchedCurrency1 = cryptoCurrencyList?.find(
                        x => x.currencyShortName === fromSymbol,
                    );
                    matchedCurrency2 = cryptoCurrencyList?.find(
                        x => x.currencyShortName === toSymbol,
                    );
                }

                if (isFX || isExchange) {
                    return (
                        <span className="inline-flex items-center gap-2  mr-3">
                            {matchedCurrency1?.icon && (
                                <img
                                    src={`${image_base_url}${matchedCurrency1.icon}`}
                                    alt={fromSymbol}
                                    className="w-5 h-5"
                                />
                            )}
                            <span>{fromSymbol}</span>
                            <span>-</span>
                            {matchedCurrency2?.icon && (
                                <img
                                    src={`${image_base_url}${matchedCurrency2.icon}`}
                                    alt={toSymbol}
                                    className="w-5 h-5"
                                />
                            )}
                            <span>{toSymbol}</span>
                        </span>
                    );
                }

                let matchedCurrency;

                if (row.currencyType === 'crypto') {
                    if (activeCurrencyPage === 'fiat') {
                        matchedCurrency = fiatCurrencyList?.find(
                            x => x.currencyShortName === row.transactionDetails?.[0]?.fiatCurrency,
                        );
                    } else {
                        matchedCurrency = cryptoCurrencyList?.find(
                            x => x.currencyShortName === row.currency,
                        );
                    }
                } else {
                    matchedCurrency = fiatCurrencyList?.find(
                        x => x.currencyShortName === row.currency,
                    );
                }
                return (
                    <span className="inline-flex items-center gap-2">
                        {matchedCurrency?.currencyIcon && (
                            <img
                                src={`${image_base_url}${matchedCurrency.currencyIcon}`}
                                alt={matchedCurrency.currencyShortName}
                                className="w-5 h-5"
                            />
                        )}
                        <span>{matchedCurrency?.currencyShortName || row.currency}</span>
                    </span>
                );
            }

            if (col.key === 'amount') {
                let content = 'N/A';
                const transaction = row.transactionDetails[0];

                if (row.currencyType === 'fiat') {
                    if (row.transactionDetails && row.transactionDetails.length > 0) {
                        if (row.type === 'FX' && transaction.fromAmount && transaction.toAmount) {
                            content = (
                                <div>
                                    <p>
                                        {getCurrencySymbol(transaction.fromCurrency)}{' '}
                                        {toFixedTrunc(transaction.fromAmount, 2)} - <br />
                                        {getCurrencySymbol(transaction.toCurrency)}{' '}
                                        {toFixedTrunc(transaction.toAmount, 2)}
                                    </p>
                                </div>
                            );
                        } else if (transaction.amount) {
                            const depositAmount =
                                row?.type === 'Deposit' &&
                                transaction?.amount - row?.transactionFee;
                            content = (
                                <div>
                                    {getBalanceSign(row)}{' '}
                                    {getCurrencySymbol(row?.currency || transaction?.currency)}{' '}
                                    {row.type !== 'Deposit'
                                        ? toFixedTrunc(transaction?.amount, 2)
                                        : toFixedTrunc(depositAmount, 2)}
                                </div>
                            );
                        }
                    }
                } else if (row.currencyType === 'crypto') {
                    content = (
                        <div>
                            {row.type === 'Exchange' ? (
                                <>
                                    {getCurrencySymbol(transaction.fromCryptoId)}{' '}
                                    {toFixedTrunc(transaction.fromCryptoAmount, 8)}
                                    {' - '}
                                    <br />
                                    {getCurrencySymbol(transaction.toCryptoId)}{' '}
                                    {toFixedTrunc(transaction.toCryptoAmount, 8)}
                                </>
                            ) : (
                                <>
                                    {activeCurrencyPage === 'fiat' ? (
                                        <>
                                            {getCurrencySymbol(
                                                row.transactionDetails[0]?.fiatCurrency,
                                            )}{' '}
                                            {toFixedTrunc(row.transactionDetails[0]?.fiatAmount, 2)}
                                        </>
                                    ) : (
                                        <>
                                            {getBalanceSign(row)}{' '}
                                            {getCurrencySymbol(transaction.cryptoId)}{' '}
                                            {toFixedTrunc(transaction.cryptoAmount, 8)}
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    );
                } else {
                    content = (
                        <div>
                            {getBalanceSign(row)}{' '}
                            {getCurrencySymbol(row?.currency || transaction?.currency)}{' '}
                            {toFixedTrunc(transaction?.cryptoAmount, 2)}
                        </div>
                    );
                }
                return content;
            }
            return null;
        },
        renderNestedTransactionRow: row => <TransactionExpanded row={row} />,
    };

    const isTransactionsPage = useLocation().pathname.includes('/client/transactions');

    if (isDashboard && transformedStatementsData?.length === 0) {
        return null;
    }

    return (
        <div className={`${activeCurrencyPage === 'crypto' && !isTransactionsPage ? 'm-1' : ""}`}>
            <div className="lg:flex flex-col hidden">
                {isDashboard && activeCurrencyPage === 'crypto' && (
                    <div className="flex flex-col bg-[#FFFFFF] dark:bg-[#26272B] md:flex-row lg:items-center justify-between items-center px-6 !py-4 rounded-lg rounded-b-none  border !border-section-br">
                        <h2 className="font-semibold text-lg sm:text-sm leading-7 text-left w-full md:w-auto !text-section-title ">
                            TRANSACTIONS
                        </h2>
                        <div className="flex justify-end gap-2">
                            <JunoButton
                                className="!w-[66px] text-xs !p-0 !h-7 font-semibold"
                                type="secondary"
                                onClick={handleSeeAll}>
                                See all
                            </JunoButton>
                        </div>
                    </div>
                )}

                {transformedStatementsData?.length === 0 ? (
                    <EmptyStateScreen text="There are no transactions that match your criteria." />
                ) : (
                    <DataTable
                        data={transformedStatementsData || []}
                        columns={overviewTableConfig.columns}
                        hasNestedRows={true}
                        renderNestedRow={overviewTableConfig.renderNestedTransactionRow}
                        customRenderCell={overviewTableConfig.customRenderCell}
                        className={!isDashboard && 'rounded-b-none'}
                    />
                )}
                {!isDashboard && transformedStatementsData?.length > 0 && (
                    <div className="flex justify-center py-4 !bg-modal-bg">
                        <Pagination
                            data={data}
                            pageSize={limit}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            totalCount={totalTransactions}
                        />
                    </div>
                )}
            </div>
            <TransactionMobileComponent
                data={data}
                isDashboard={isDashboard}
                activeCurrencyPage={activeCurrencyPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalCount={totalTransactions}
                pageSize={limit}
            />
        </div>
    );
};

TransactionsHistoryTable.propTypes = {
    isDashboard: PropTypes.bool.isRequired,
};

export default TransactionsHistoryTable;
